/**
 * Authentication Context Provider - Clerk Integration
 *
 * Features:
 * - Wrapper around Clerk's auth hooks
 * - Maintains same interface for backward compatibility
 * - No token management (handled by Clerk)
 * - No CORS issues (handled by Clerk)
 * - Automatic session management
 */

import React, { createContext, useContext, ReactNode } from 'react';
import { useAuth as useClerkAuth, useUser } from '@clerk/clerk-react';

// User type (extended for application needs)
export interface User {
  id: string;
  email: string;
  full_name?: string;
  firstName?: string;
  lastName?: string;
  imageUrl?: string;
  role?: string;
  tier?: 'free' | 'developer' | 'professional' | 'business' | 'free_tier' | 'basic';
  subscription_tier?: 'free' | 'developer' | 'professional' | 'business' | 'free_tier' | 'basic';
  is_verified?: boolean;
  email_verified?: boolean;
  preferred_model?: string;
  oauth_provider?: string;
  created_at?: string;
}

// Auth context state
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => Promise<void>;
  // Deprecated functions (kept for backward compatibility, but will throw errors)
  login?: (email: string, password: string) => Promise<void>;
  loginWithGoogle?: () => Promise<void>;
  loginWithMicrosoft?: () => Promise<void>;
  signup?: (email: string, password: string, fullName: string, refCode?: string) => Promise<void>;
  error?: null;
  clearError?: () => void;
  refreshUser?: () => Promise<void>;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider props
interface AuthProviderProps {
  children: ReactNode;
}

// Provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { isSignedIn, isLoaded, signOut } = useClerkAuth();
  const { user: clerkUser } = useUser();

  // Convert Clerk user to our User type
  const user: User | null = clerkUser
    ? {
        id: clerkUser.id,
        email: clerkUser.primaryEmailAddress?.emailAddress || '',
        full_name: clerkUser.fullName || undefined,
        firstName: clerkUser.firstName || undefined,
        lastName: clerkUser.lastName || undefined,
        imageUrl: clerkUser.imageUrl,
      }
    : null;

  const logout = async () => {
    await signOut();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: isSignedIn || false,
        isLoading: !isLoaded,
        logout,
        // Deprecated - will throw error if called
        login: async () => {
          throw new Error('login() is deprecated. Use Clerk SignIn component at /login');
        },
        loginWithGoogle: async () => {
          throw new Error('loginWithGoogle() is deprecated. Use Clerk SignIn component');
        },
        loginWithMicrosoft: async () => {
          throw new Error('loginWithMicrosoft() is deprecated. Use Clerk SignIn component');
        },
        signup: async () => {
          throw new Error('signup() is deprecated. Use Clerk SignUp component at /register');
        },
        error: null,
        clearError: () => {},
        refreshUser: async () => {
          // Clerk automatically refreshes user data
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
